import React from 'react';
import { formatEther, formatAddress } from '../../services/web3';
import { getTransactionUrl } from '../../services/blockchain/explorer';

export interface Transaction {
  id: string;
  type: 'deposit' | 'approve' | 'release' | 'create' | 'dispute' | 'arbitrator' | 'resolve';
  txHash?: string;
  amount?: string; // in wei
  milestoneIndex?: number;
  milestoneDescription?: string;
  from: string;
  to?: string;
  timestamp: string;
  blockNumber?: number;
  clientPercent?: number;
  providerPercent?: number;
}

interface TransactionHistoryProps {
  transactions: Transaction[];
  projectId?: string;
  blockchainProjectId?: number | null;
  clientAddress?: string;
  providerAddress?: string;
}

const TransactionHistory: React.FC<TransactionHistoryProps> = ({
  transactions,
  projectId,
  blockchainProjectId,
  clientAddress,
  providerAddress,
}) => {
  const getTransactionIcon = (type: Transaction['type']) => {
    switch (type) {
      case 'deposit':
      case 'approve':
      case 'release':
      case 'create':
      default:
        return '';
    }
  };

  const getTransactionLabel = (type: Transaction['type']) => {
    switch (type) {
      case 'deposit':
        return 'Funds Deposited';
      case 'approve':
        return 'Milestone Approved';
      case 'release':
        return 'Payment Released';
      case 'create':
        return 'Project Created';
      case 'dispute':
        return 'Dispute Opened';
      case 'arbitrator':
        return 'Arbitrator Assigned';
      case 'resolve':
        return 'Dispute Resolved';
      default:
        return 'Transaction';
    }
  };

  const getTransactionDescription = (tx: Transaction) => {
    switch (tx.type) {
      case 'deposit':
        return tx.amount
          ? `Deposited ${parseFloat(formatEther(tx.amount)).toFixed(4)} MATIC to escrow`
          : 'Deposited funds to escrow';
      case 'approve':
        return tx.milestoneDescription
          ? `Approved milestone: "${tx.milestoneDescription}"`
          : tx.milestoneIndex !== undefined
          ? `Approved milestone ${tx.milestoneIndex + 1}`
          : 'Approved milestone';
      case 'release':
        return tx.milestoneDescription
          ? `Released payment for: "${tx.milestoneDescription}"`
          : tx.milestoneIndex !== undefined
          ? `Released payment for milestone ${tx.milestoneIndex + 1}`
          : 'Released payment';
      case 'create':
        return 'Project was created on blockchain';
      case 'dispute':
        return tx.milestoneDescription
          ? `Opened dispute on milestone: "${tx.milestoneDescription}"`
          : tx.milestoneIndex !== undefined
          ? `Opened dispute on milestone ${tx.milestoneIndex + 1}`
          : 'Opened dispute';
      case 'arbitrator':
        return tx.to
          ? `Assigned arbitrator: ${formatAddress(tx.to)}`
          : 'Assigned arbitrator to project';
      case 'resolve':
        const clientPct = tx.clientPercent ?? 0;
        const providerPct = tx.providerPercent ?? 0;
        const desc = tx.milestoneDescription
          ? `Resolved dispute for: "${tx.milestoneDescription}"`
          : tx.milestoneIndex !== undefined
          ? `Resolved dispute for milestone ${tx.milestoneIndex + 1}`
          : 'Resolved dispute';
        return `${desc} (${clientPct}% client, ${providerPct}% provider)`;
      default:
        return 'Transaction executed';
    }
  };

  const getTransactionColor = (type: Transaction['type']) => {
    switch (type) {
      case 'deposit':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'approve':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'release':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'create':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'dispute':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'arbitrator':
        return 'bg-indigo-50 text-indigo-700 border-indigo-200';
      case 'resolve':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getRoleBadge = (address: string) => {
    if (!address || (!clientAddress && !providerAddress)) return null;
    
    const normalizedAddress = address.toLowerCase();
    const isClient = clientAddress && normalizedAddress === clientAddress.toLowerCase();
    const isProvider = providerAddress && normalizedAddress === providerAddress.toLowerCase();
    
    if (isClient) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
          Client
        </span>
      );
    }
    if (isProvider) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
          Provider
        </span>
      );
    }
    return null;
  };

  const formatDateTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    // Format date
    const dateStr = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

    // Format time
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });

    // Relative time
    let relativeTime = '';
    if (diffMins < 1) {
      relativeTime = 'Just now';
    } else if (diffMins < 60) {
      relativeTime = `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      relativeTime = `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      relativeTime = `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else {
      relativeTime = dateStr;
    }

    return {
      date: dateStr,
      time: timeStr,
      relative: relativeTime,
      full: `${dateStr} at ${timeStr}`,
    };
  };

  if (transactions.length === 0) {
    return (
      <div className="bg-white border border-[#471b001e] rounded-2xl p-6">
        <h3 className="text-xl font-semibold text-[#471b00] font-poly mb-4">
          Transaction History
        </h3>
        <p className="text-sm text-[#471b007f] font-dm text-center py-8">
          No transactions yet
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#471b001e] rounded-2xl p-6">
      <h3 className="text-xl font-semibold text-[#471b00] font-poly mb-6">
        Transaction History
      </h3>
      <div className="space-y-4">
        {transactions.map((tx) => {
          const dateTime = formatDateTime(tx.timestamp);
          const roleBadge = getRoleBadge(tx.from);
          
          return (
            <div
              key={tx.id}
              className={`border-2 rounded-xl p-5 ${getTransactionColor(tx.type)} transition-all hover:shadow-md`}
            >
              {/* Header: Date & Time */}
              <div className="flex items-center justify-between mb-3 pb-3 border-b border-current/20">
                <div>
                  <div className="text-xs font-medium opacity-60 mb-1">
                    {dateTime.relative}
                  </div>
                  <div className="text-sm font-semibold">
                    {dateTime.full}
                  </div>
                </div>
                <div className="text-3xl">{getTransactionIcon(tx.type)}</div>
              </div>

              {/* Main Content */}
              <div className="space-y-2">
                {/* Action */}
                <div>
                  <h4 className="text-base font-semibold mb-1">
                    {getTransactionLabel(tx.type)}
                  </h4>
                  <p className="text-sm opacity-90">
                    {getTransactionDescription(tx)}
                  </p>
                </div>

                {/* Amount (if applicable) */}
                {tx.amount && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium opacity-75">Amount:</span>
                    <span className="text-base font-bold">
                      {parseFloat(formatEther(tx.amount)).toFixed(4)} MATIC
                    </span>
                  </div>
                )}

                {/* Milestone info */}
                {tx.milestoneIndex !== undefined && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium opacity-75">Milestone:</span>
                    <span className="text-sm font-medium">
                      #{tx.milestoneIndex + 1}
                    </span>
                  </div>
                )}

                {/* Performed by */}
                <div className="flex items-center gap-2 pt-2 border-t border-current/20">
                  <span className="text-xs font-medium opacity-75">Performed by:</span>
                  <span className="text-xs font-mono font-medium">
                    {formatAddress(tx.from)}
                  </span>
                  {roleBadge}
                </div>

                {/* Transaction link */}
                {tx.txHash && (
                  <div className="pt-2">
                    <a
                      href={getTransactionUrl(tx.txHash)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/70 hover:bg-white text-xs font-medium rounded-lg transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span>🔗</span>
                      <span>View on Blockchain Explorer</span>
                    </a>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TransactionHistory;

